import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import type {
  Connection,
  ConnectorType,
  DraftTestResult,
} from '@/shared/api/generated/catalog';
import { ApiError } from '@/shared/api/http';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StepTrail } from '@/shared/ui/StepTrail';

import { CONNECTOR_LABEL, useConnections } from '@/entities/connection';

import { useSetAutoProfileEnabled } from '../api/autoProfile';
import {
  useRegisterConnection,
  useRequestHarvest,
  useTestDraft,
  useVerifyConnection,
} from '../api/connections';
import type { DraftSource } from '../api/connections';
import { useConnectors } from '../api/connectors';
import { versionReading } from '../model/version';
import { canReturnTo, checkStates, problemsIn, testOutcome } from '../model/register';
import type {
  EndpointDraft,
  FieldProblem,
  RegisterStep,
  VerifyReading,
} from '../model/register';
import { credentialPayload, defaultPortFor, fieldsFor } from '../model/registration';
import type { Mechanism } from '../model/registration';
import { CredentialStep, DatasetsStep, SourceTypeStep, TestStep } from './RegisterSteps';
import type { PreviewReading } from './RegisterSteps';

/* Registering a source, as the design's four-step dialog: choose a type, give its credential, watch
 * it tested, then harvest it now or later and land on the source's own page.
 *
 * **Nothing is stored until the test passes.** Step 2 does not register: it tests the draft through
 * `POST …/connections/test`, which reaches the source with the credential held in memory and writes
 * no row and no secret. A refused test therefore leaves nothing behind — the reader edits the
 * credential, every field still theirs to change, and tests again. The source is registered, its
 * credential sealed into Vault, only when the reader finishes from step 4, which step 3 opens only
 * for a passing test. Then it is verified once more on the stored credential — so the row records
 * `verified` from the platform's own check of what it stored rather than from a test of a draft —
 * and harvested now or left for later.
 *
 * **What each step can honestly claim.** The tiles are `GET /v1/catalog/connectors`, so an
 * unavailable family is shown and explained rather than hidden. The fields are that profile's own,
 * so a form is the server's answer rather than a shape per provider. The test's four rows come from
 * the server naming the step that refused, so a tick is never drawn for something nobody measured.
 * And the datasets step states what a harvest covers rather than offering a selection the harvest
 * would ignore — `POST …/harvest` takes no scope.
 *
 * **Credential handling is the old form's, unchanged.** Values live in this component's state and
 * nowhere else; secret inputs are uncontrolled so a credential never reaches the serialised DOM; a
 * change of type clears them rather than merging (three families share `username` and `password`);
 * and once the source is registered every value is dropped and both mutations' observers are reset,
 * because React Query keeps a submitted credential in `variables` until told otherwise.
 */

export function RegisterSourceDialog({
  open,
  onClose,
  onRegistered,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Called once the source exists, so the screen behind can confirm it if the dialog is closed. */
  readonly onRegistered: (connection: Connection) => void;
}): ReactElement | null {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      title={t('register.title')}
      hint={t('register.subtitle')}
      onClose={onClose}
      size="wide"
      className="lq-rg"
    >
      {/* Its own component so its state is born and dies with the dialog: `Modal` renders nothing
          while closed, and a second opening starts clean rather than at the last attempt's step. */}
      <RegisterFlow onClose={onClose} onRegistered={onRegistered} />
    </Modal>
  );
}

const EMPTY_DRAFT: EndpointDraft = { name: '', host: '', port: '', database: '' };

const RESIDENCY_REFUSAL = 'https://lumiq.dev/problems/residency-breach';

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function RegisterFlow({
  onClose,
  onRegistered,
}: {
  readonly onClose: () => void;
  readonly onRegistered: (connection: Connection) => void;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const connectors = useConnectors();
  const connections = useConnections();
  const testDraft = useTestDraft();
  const register = useRegisterConnection();
  const verify = useVerifyConnection();
  const harvest = useRequestHarvest();
  const enableAuto = useSetAutoProfileEnabled();

  const [step, setStep] = useState<RegisterStep>(1);
  /* `null` until step 1 is answered. Not defaulted: a pre-chosen tile is a choice the reader did not
     make, and the choice decides which credential they are about to type. */
  const [connector, setConnector] = useState<ConnectorType | null>(null);
  const [mechanism, setMechanism] = useState<Mechanism>('password');
  const [draft, setDraft] = useState<EndpointDraft>(EMPTY_DRAFT);
  const [values, setValues] = useState<Record<string, string>>({});
  /* Bumped whenever the secret inputs must empty — they are uncontrolled, so a remount is what
     clears them. A counter rather than the type, because returning to edit must clear them with the
     type unchanged. */
  const [secretGeneration, setSecretGeneration] = useState(0);
  const [problems, setProblems] = useState<Readonly<Record<string, FieldProblem>>>({});
  /* Set only by finishing, and kept so a finish that registered the source and then failed at the
     harvest is retried from where it stopped — never by registering a second time. */
  const [registered, setRegistered] = useState<Connection | null>(null);
  /* The last test's answer, kept apart from the mutation so the mutation can be reset — dropping the
     credential it holds in `variables` — once the source is registered, without losing what the
     test found. */
  const [tested, setTested] = useState<DraftTestResult | null>(null);
  const [auto, setAuto] = useState(true);
  const [finishing, setFinishing] = useState<'now' | 'later' | null>(null);
  const [failure, setFailure] = useState<{ title: string; detail: string } | null>(null);

  const profiles = useMemo(() => connectors.data?.items ?? [], [connectors.data]);
  const profile = profiles.find((entry) => entry.connector_type === connector);
  const fields = useMemo(
    () => (profile === undefined ? [] : fieldsFor(profile, mechanism)),
    [profile, mechanism],
  );
  const secretFields = useMemo(
    () => new Set(fields.filter((field) => field.secret).map((field) => field.name)),
    [fields],
  );

  /* ------------------------------------------------------------------ the test, derived */

  const result = testDraft.isPending || tested === null ? undefined : tested;
  const verifyReading: VerifyReading = testDraft.isError
    ? { kind: 'unasked' }
    : result === undefined
      ? { kind: 'running' }
      : {
          kind: 'answered',
          reachable: result.reachable,
          failedStage: result.failed_stage,
        };
  /* The catalogue is listed by the same request, so a passing result has passed all four. */
  const states = checkStates(
    verifyReading,
    result?.reachable === true ? 'passed' : 'idle',
  );
  const outcome = testOutcome(states, testDraft.isError);

  const draftSource = (): DraftSource | null => {
    if (profile === undefined || connector === null) return null;
    const port = Number.parseInt(draft.port, 10);
    return {
      connector_type: connector,
      /* A family reached through its credential still fills the row's three address columns, with
         what identifies it — a Snowflake account, a Databricks workspace host. */
      host: profile.needs_endpoint
        ? draft.host.trim()
        : (values['host'] ?? values['account'] ?? '—'),
      port: profile.needs_endpoint && Number.isFinite(port) ? port : 443,
      database: draft.database.trim(),
      credentials: credentialPayload(fields, values),
    };
  };

  const runTest = (): void => {
    const source = draftSource();
    if (source === null) return;
    setStep(3);
    setTested(null);
    testDraft.mutate(source, { onSuccess: setTested });
  };

  /* ------------------------------------------------------------------ step 1 */

  const chooseConnector = (next: ConnectorType): void => {
    setConnector(next);
    setMechanism('password');
    /* Cleared on every change of type, never merged — see the note at the top. */
    setValues({});
    setSecretGeneration((generation) => generation + 1);
    setProblems({});
    setTested(null);
    testDraft.reset();
    const suggested = defaultPortFor(next);
    setDraft((current) => ({
      ...current,
      port: suggested === null ? '' : String(suggested),
    }));
  };

  /* ------------------------------------------------------------------ step 2 */

  const submitCredential = (): void => {
    if (profile === undefined) return;
    const found = problemsIn({
      draft,
      profile,
      fields,
      values,
      existingNames: (connections.data?.items ?? []).map((each) => each.name),
    });
    setProblems(found);
    if (Object.keys(found).length > 0) return;
    runTest();
  };

  /** Back to the form to change something. Every field stays the reader's to edit, because nothing
   * has been stored; the secret ones empty and must be typed again, since an uncontrolled input that
   * looked empty while still holding a value would be submitted unseen. */
  const editCredential = (): void => {
    setValues((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([name]) => !secretFields.has(name)),
      ),
    );
    setSecretGeneration((generation) => generation + 1);
    setProblems({});
    setFailure(null);
    setTested(null);
    testDraft.reset();
    setStep(2);
  };

  /* ------------------------------------------------------------------ step 4 */

  const finish = async (harvestNow: boolean): Promise<void> => {
    const source = draftSource();
    if (source === null) return;
    setFailure(null);
    setFinishing(harvestNow ? 'now' : 'later');

    let stored = registered;
    if (stored === null) {
      try {
        stored = await register.mutateAsync({ ...source, name: draft.name.trim() });
      } catch (error) {
        setFailure({ title: t('connections.register.failed'), detail: messageOf(error) });
        setFinishing(null);
        return;
      }
      /* Registered: the credential is sealed now, so every copy this dialog holds goes. */
      setRegistered(stored);
      onRegistered(stored);
      setValues({});
      setSecretGeneration((generation) => generation + 1);
      register.reset();
      testDraft.reset();
    }

    /* The stored credential is checked once more, so the source records `verified` from the
       platform's own check of what it stored. A harvest needs a verified source. */
    let reachable = false;
    try {
      reachable = (await verify.mutateAsync(stored.id)).reachable;
    } catch {
      reachable = false;
    }

    if (reachable && auto && profile?.profiles_columns === true) {
      try {
        await enableAuto.mutateAsync({ connectionId: stored.id, enabled: true });
      } catch (error) {
        setFailure({ title: t('register.auto.failed'), detail: messageOf(error) });
        setFinishing(null);
        return;
      }
    }
    if (reachable && harvestNow) {
      try {
        await harvest.mutateAsync(stored.id);
      } catch (error) {
        setFailure({ title: t('register.harvestRefused'), detail: messageOf(error) });
        setFinishing(null);
        return;
      }
    }
    onClose();
    navigate(generatePath(SCREEN_PATHS.connection, { connectionId: stored.id }), {
      state: { registered: stored.name, harvesting: reachable && harvestNow },
    });
  };

  const schemas = result?.schemas ?? [];
  const previewReading: PreviewReading = !result?.reachable
    ? { kind: 'notRead' }
    : {
        kind: 'ready',
        schemas,
        totalSchemas: schemas.length,
        totalObjects:
          result.objects ?? schemas.reduce((sum, each) => sum + each.objects, 0),
      };

  /* ------------------------------------------------------------------ the rail */

  const none = t('register.sub.none');
  const trail = [
    {
      title: t('register.step.source'),
      sub:
        profile === undefined
          ? t('register.sub.chooseType')
          : CONNECTOR_LABEL[profile.connector_type],
    },
    {
      title: t('register.step.credentials'),
      sub: step > 2 && draft.name.trim() !== '' ? draft.name.trim() : none,
    },
    {
      title: t('register.step.test'),
      sub:
        step < 3
          ? none
          : outcome === 'reachable'
            ? t('register.sub.reachable')
            : outcome === 'refused'
              ? t('register.sub.refused')
              : outcome === 'running'
                ? t('register.sub.testing')
                : none,
    },
    {
      title: t('register.step.datasets'),
      sub: step === 4 ? t('register.sub.harvest') : none,
    },
  ];

  const goTo = (target: number): void => {
    const next = target as RegisterStep;
    if (!canReturnTo(next, step, registered !== null)) return;
    if (next === 2) {
      editCredential();
      return;
    }
    setFailure(null);
    setStep(next);
  };

  const testError = testDraft.isError ? testDraft.error : null;
  /* A residency refusal is a 403 the tenant's own policy produced, and it is explained rather than
     shown bare: a source cannot be placed in a region before it exists, so the reader needs to know
     the test cannot pass from here. */
  const residencyRefused =
    testError instanceof ApiError && testError.extras['type'] === RESIDENCY_REFUSAL;

  return (
    <div className="lq-rg__flow">
      <StepTrail
        label={t('register.rail')}
        steps={trail}
        current={step}
        onStep={goTo}
        canReturn={(target) =>
          canReturnTo(target as RegisterStep, step, registered !== null)
        }
        marks="ticks"
      />

      <div className="lq-rg__body" key={step}>
        {step === 1 ? (
          connectors.isPending ? (
            <div className="lq-rg__loading">
              <p className="lq-rg__note">{t('register.source.loading')}</p>
              <SkeletonRows rows={2} columns={3} />
            </div>
          ) : connectors.isError || profiles.length === 0 ? (
            <Alert tone="error" title={t('connections.register.catalogueFailed')}>
              {t('connections.register.catalogueFailedBody')}
            </Alert>
          ) : (
            <>
              <h3 className="lq-rg__subhead">{t('register.source.heading')}</h3>
              <SourceTypeStep
                profiles={profiles}
                chosen={connector}
                onChoose={chooseConnector}
              />
              <div className="lq-rg__actions">
                <Button type="button" onClick={onClose}>
                  {t('register.cancel')}
                </Button>
                <Button
                  tone="primary"
                  type="button"
                  disabled={connector === null}
                  onClick={() => {
                    setStep(2);
                  }}
                >
                  {t('register.continue')}
                </Button>
              </div>
            </>
          )
        ) : null}

        {step === 2 && profile !== undefined ? (
          <CredentialStep
            profile={profile}
            draft={draft}
            onDraft={(field, value) => {
              setDraft((current) => ({ ...current, [field]: value }));
              setProblems((current) => withoutProblem(current, field));
            }}
            fields={fields}
            values={values}
            onValue={(name, value) => {
              setValues((current) => ({ ...current, [name]: value }));
              setProblems((current) => withoutProblem(current, name));
            }}
            mechanism={mechanism}
            onMechanism={(next) => {
              setMechanism(next);
              /* The other mechanism's value is dropped rather than kept for a toggle back: the
                 domain refuses a credential carrying both. */
              setValues((current) =>
                Object.fromEntries(
                  Object.entries(current).filter(
                    ([name]) => name !== 'password' && name !== 'private_key_pem',
                  ),
                ),
              );
              setSecretGeneration((generation) => generation + 1);
            }}
            problems={problems}
            secretKey={`${String(connector)}:${mechanism}:${String(secretGeneration)}`}
            onSubmit={submitCredential}
            onBack={() => {
              setStep(1);
            }}
          />
        ) : null}

        {step === 3 ? (
          <TestStep
            name={draft.name.trim()}
            states={states}
            outcome={outcome}
            failedStage={result?.failed_stage ?? null}
            detail={result?.detail ?? null}
            version={versionReading(result?.source_version ?? null)}
            product={connector === null ? '' : CONNECTOR_LABEL[connector]}
            requestError={
              testError === null
                ? null
                : residencyRefused
                  ? `${t('register.test.residency')} ${testError.message}`
                  : testError.message
            }
            onRetry={runTest}
            onEdit={editCredential}
            onContinue={() => {
              setStep(4);
            }}
          />
        ) : null}

        {step === 4 ? (
          <DatasetsStep
            preview={previewReading}
            reachable={outcome === 'reachable' || registered !== null}
            profilingOffered={profile?.profiles_columns === true}
            auto={auto}
            onAuto={setAuto}
            finishing={finishing}
            failure={failure}
            locked={registered !== null}
            onHarvestNow={() => {
              void finish(true);
            }}
            onHarvestLater={() => {
              void finish(false);
            }}
            onBack={() => {
              setStep(3);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

function withoutProblem(
  problems: Readonly<Record<string, FieldProblem>>,
  field: string,
): Readonly<Record<string, FieldProblem>> {
  if (!(field in problems)) return problems;
  return Object.fromEntries(Object.entries(problems).filter(([name]) => name !== field));
}
